/* PWA (DEV-06, PLAT-02): service worker registration and the install prompt.
   The worker is `public/sw.js` (own implementation, no Workbox): offline app shell and hashed assets only.
   It never caches /api: chat history reads are cached by the settings feature (features/settings/offline.ts,
   IndexedDB through the API fetcher), so there is one cache per kind of data and they never disagree. */
import { useSyncExternalStore } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/** Starts listening for `beforeinstallprompt` (call once, early, so the event is not missed). */
export function captureInstallPrompt(win: Window = window): void {
  win.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    emit();
  });
  win.addEventListener('appinstalled', () => { deferred = null; installed = true; emit(); });
  try { installed = win.matchMedia?.('(display-mode: standalone)').matches || win.matchMedia?.('(display-mode: window-controls-overlay)').matches || false; } catch { /* old browsers */ }
}

export interface InstallState { canInstall: boolean; installed: boolean }
let snapshot: InstallState = { canInstall: false, installed: false };
function getSnapshot(): InstallState {
  const next = { canInstall: !!deferred && !installed, installed };
  if (next.canInstall !== snapshot.canInstall || next.installed !== snapshot.installed) snapshot = next;
  return snapshot;
}
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

/** `{canInstall, installed, install}`: show "Install app" only while the browser offers it. */
export function useInstallPrompt(): InstallState & { install: () => Promise<'accepted' | 'dismissed' | 'unavailable'> } {
  const s = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { ...s, install: promptInstall };
}

export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const e = deferred;
  if (!e) return 'unavailable';
  deferred = null;
  emit();
  await e.prompt();
  const { outcome } = await e.userChoice;
  if (outcome === 'accepted') { installed = true; emit(); }
  return outcome;
}

/** Tests. */
export function __setInstallEvent(e: BeforeInstallPromptEvent | null, isInstalled = false): void { deferred = e; installed = isInstalled; emit(); }

/** Registers /sw.js in production builds (never in dev or tests, where it would cache the dev server). */
export function registerServiceWorker(opts: { force?: boolean; nav?: Navigator } = {}): Promise<ServiceWorkerRegistration | null> {
  const nav = opts.nav ?? (typeof navigator !== 'undefined' ? navigator : undefined);
  if (!nav || !('serviceWorker' in nav)) return Promise.resolve(null);
  if (!opts.force && !import.meta.env.PROD) return Promise.resolve(null);
  return nav.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => null);
}
